import { Typography } from "@mui/material";
import GooglePlacesAutocomplete, {
  geocodeByPlaceId,
} from "react-google-places-autocomplete";
import { useEffect, useMemo, useState } from "react";

export interface ILocationResponsePayload {
  country: string;
  address_line1: string;
  address_line2: string;
  city: string;
  state: string;
  postal_code: string;
  region: string;
  fullAddress?: string;
}

interface ILocationAutoCompleteProps {
  onLocationChange: (data: ILocationResponsePayload) => void;
  id?: string | number;
  value: string | number | null;
  [key: string]: any;
  error?: boolean;
  helperText?: string;
}

export const LocationAutoCompleteWithManualAddress = (
  props: ILocationAutoCompleteProps
) => {
  const [inputValue, setInputValue] = useState(props.value?.toString() || "");
  const selectedOption = useMemo(() => {
    if (!props.value) return null;
    const label = props.value.toString();
    return { label, value: label };
  }, [props.value]);

  useEffect(() => {
    setInputValue(props.value?.toString() || "");
  }, [props.value]);

  const handleManualAddress = (manualAddress: string) => {
    setInputValue(manualAddress);
    props.onLocationChange({
      country: "",
      address_line1: "",
      address_line2: "",
      city: "",
      state: "",
      postal_code: "",
      region: "",
      fullAddress: manualAddress,
    });
  };

  return (
    <>
      <GooglePlacesAutocomplete
        apiKey={process.env.REACT_APP_GOOGLE_MAP_API_KEY}
        key={props.value}
        selectProps={{
          innerProps: {
            id: props.id,
          },
          placeholder: "Choose Location or Type Your Address",
          isClearable: true,
          defaultInputValue: props.value?.toString(),
          value: selectedOption,
          inputValue: inputValue,
          onInputChange: (value: string) => {
            setInputValue(value);
          },
          onChange: async (place: any) => {
            // Use exact same logic as LocationAutoComplete.tsx for normal selections
            const placeId = place?.value?.place_id;

            if (!placeId) {
              if (!place) {
                setInputValue("");
                props.onLocationChange({
                  country: "",
                  address_line1: "",
                  address_line2: "",
                  city: "",
                  state: "",
                  postal_code: "",
                  region: "",
                  fullAddress: "",
                });
                return;
              }
              // Handle manual address entry when no Google Places selection
              if (inputValue && inputValue.trim()) {
                handleManualAddress(inputValue.trim());
              }
              return;
            }

            try {
              // Get the detailed place information using the placeId (same logic as LocationAutoComplete.tsx)
              const response = await geocodeByPlaceId(placeId);

              console.log("response....", response);
              const { address_components } = response[0];

              let fullAddress = "";
              let city = "";
              let state = "";
              let country = "";
              let zipCode = "";
              let addressLine1 = "";
              let addressLine2 = "";

              // Extract city, state, country, zip code, and address from the address_components
              for (const component of address_components) {
                const types = component.types;
                if (types.includes("locality")) {
                  city = component.long_name;
                } else if (types.includes("administrative_area_level_1")) {
                  state = component.long_name;
                } else if (types.includes("country")) {
                  country = component.long_name;
                } else if (types.includes("postal_code")) {
                  zipCode = component.long_name;
                } else if (
                  types.includes("street_number") ||
                  types.includes("route") ||
                  types.includes("premise")
                ) {
                  addressLine1 += component.long_name + " ";
                } else if (
                  types.includes("neighborhood") ||
                  types.includes("sublocality") ||
                  types.includes("sublocality_level_1") ||
                  types.includes("sublocality_level_2")
                ) {
                  addressLine2 += component.long_name + " ";
                }
              }

              // Build fullAddress from address components if not available from place label
              fullAddress = [addressLine1.trim(), addressLine2.trim()]
                .filter(Boolean)
                .join(", ");

              const formattedAddress = (
                place.label ||
                fullAddress.trim() ||
                ""
              ).trim();
              setInputValue(formattedAddress);
              props.onLocationChange({
                country: country,
                address_line1: addressLine1.trim(),
                address_line2: addressLine2.trim(),
                city: city,
                state: state,
                postal_code: zipCode,
                region: "IND",
                fullAddress: formattedAddress,
              });
            } catch (error) {
              console.error("Error fetching geocode data:", error);
              // Fallback to manual address if geocoding fails
              if (inputValue && inputValue.trim()) {
                handleManualAddress(inputValue.trim());
              } else {
                setInputValue("");
                props.onLocationChange({
                  country: "",
                  address_line1: "",
                  address_line2: "",
                  city: "",
                  state: "",
                  postal_code: "",
                  region: "",
                });
              }
            }
          },
          noOptionsMessage: ({ inputValue }: { inputValue: string }) => {
            if (inputValue && inputValue.trim()) {
              return `Press Enter to use "${inputValue}" as your address`;
            }
            return "No options found";
          },
          onKeyDown: (event: React.KeyboardEvent) => {
            if (event.key === "Enter" && inputValue && inputValue.trim()) {
              event.preventDefault();
              handleManualAddress(inputValue.trim());
            }
          },
        }}
        autocompletionRequest={{
          componentRestrictions: {
            country: ["in"],
          },
        }}
        {...props}
      />
      <Typography
        variant="body2"
        sx={{
          fontSize: "0.75rem",
          color: "#e46a76",
          marginLeft: 2,
          marginTop: 0.75,
        }}
      >
        {props.error && props.helperText}
      </Typography>
    </>
  );
};
